import React, { useContext, useMemo } from 'react';
import { Button, DialogActions, styled } from '@mui/material';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import { DELETE_COMPANY_ENABLER } from 'common/constants/features';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FeatureEnabler from 'components/FeatureEnabler';
import DialogContext from 'context/DialogContext';
import { FormDialogProps } from './FormDialog.props';
import { formDialogClasses, StyledDialog } from './FormDialog.styles';
import ConfirmationCancelDialog from '../ConfirmationCancelDialog/ConfirmationCancelDialog';
import DeleteButton from '../DeleteButton/DeleteButton';

const FormDialog = ({
  title = '',
  hideTitle,
  open,
  editMode,
  isValid,
  children,
  onSave,
  onDelete,
  deleteText,
  onClose,
  confirmCancel = false,
  subtitle,
  bigDialog = false,
  PaperProps,
  isLoading = false,
  loadingText,
  customButtonLabel,
  shouldCloseOnDelete = false,
  allowDelete,
  toolTipText,
  turnOnDeleteFunctionality,
  customActionButtonLabel,
  onCustomAction,
  showCancelButton = true,
  disableTitleClose = true,
  showSaveButton = true,
  customCancelButton,
  customButton,
  classesContent,
  shouldCheckFeatureEnabler = false,
  doPostSaveClose = true,
}: FormDialogProps) => {
  const [{ isShowLoadingProgress = false }] = useStore() as unknown as UseStoreValues;
  const { showDialog } = useContext(DialogContext);

  const handleSave = () => {
    if (onSave) {
      const result = onSave();
      if (result instanceof Promise) {
        result
          .then(aux => onClose(aux))
          .catch(() => {
            onClose(result);
          });
      } else if (doPostSaveClose) {
        onClose(result);
      }
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      if (shouldCloseOnDelete) {
        const aux = await onDelete();
        onClose(aux);
      } else {
        await onDelete();
      }
    }
  };

  const handleClose = () => {
    if (confirmCancel && !editMode) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      showDialog({
        wrapper: ConfirmationCancelDialog,
        actions: [
          {
            label: 'Cancel Anyways',
            variant: 'contained',
            type: 'danger',
            callback: onClose,
          },
        ],
      });
    } else {
      onClose();
    }
  };

  const getActionButtonText = () => {
    if (editMode) {
      return 'Update';
    }
    return 'Save';
  };

  const showCustomActionButton = customActionButtonLabel && onCustomAction;
  let showDeleteButton = !isUndefined(onDelete) && (editMode || allowDelete);

  if (turnOnDeleteFunctionality === false) {
    // Hide the button when the delete functionality is not allowed
    showDeleteButton = false;
  }

  const enableDeleteButton = useMemo(
    () => shouldCheckFeatureEnabler || allowDelete,
    [shouldCheckFeatureEnabler, allowDelete]
  );

  const displayDeleteBtn = () => {
    if (shouldCheckFeatureEnabler) {
      return (
        <FeatureEnabler enabler={DELETE_COMPANY_ENABLER} hideComponent>
          <DeleteButton
            toolTipText={toolTipText}
            allowDelete={allowDelete}
            enableDeleteButton={enableDeleteButton}
            deleteText={deleteText}
            handleDelete={handleDelete}
          />
        </FeatureEnabler>
      );
    }
    return (
      <DeleteButton
        toolTipText={toolTipText}
        allowDelete={allowDelete}
        enableDeleteButton={enableDeleteButton}
        deleteText={deleteText}
        handleDelete={handleDelete}
      />
    );
  };

  return (
    <StyledDialog
      title={title}
      hideTitle={hideTitle}
      subtitle={subtitle}
      maxWidth={bigDialog ? 'md' : 'sm'}
      fullWidth
      onClose={onClose}
      open={open}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        disableTitleClose,
      }}
      PaperProps={PaperProps}
      isLoading={isLoading}
      loadingText={loadingText}>
      <div style={classesContent || {}} className={!classesContent ? formDialogClasses.content : undefined}>
        {children}
      </div>
      <DialogActions className={formDialogClasses.actions}>
        {showDeleteButton && displayDeleteBtn()}
        {showCustomActionButton && (
          <Button
            id="custom-btn"
            className={clsx(formDialogClasses.actionButton, formDialogClasses.autoMarginRight)}
            color="primary"
            variant="text"
            onClick={onCustomAction}>
            {customActionButtonLabel}
          </Button>
        )}
        {showCancelButton && !customCancelButton && (
          <Button
            id="company-cancel-btn"
            className={formDialogClasses.actionButton}
            color="primary"
            onClick={handleClose}
            disabled={isShowLoadingProgress}>
            Cancel
          </Button>
        )}
        {customCancelButton}
        {showSaveButton && (
          <Button
            id="company-save-btn"
            className={formDialogClasses.actionButton}
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={!isValid || isShowLoadingProgress}>
            {customButtonLabel ?? getActionButtonText()}
          </Button>
        )}
        {customButton}
      </DialogActions>
    </StyledDialog>
  );
};

export default FormDialog;
